/**
 * Axios and navigation guard configuration.
 *
 * Keeps application bootstrap focused on Vue initialization while request
 * retries and normalized API errors live in this service.
 */
import axios from "axios";
import { GetIncompletePromotionPaymentRedirect } from "../helpers/promotionRouteGuardHelper";
import { AttachApiError } from "../helpers/apiErrorHelper";

let csrfRefreshPromise = null;

/**
 * Configures axios defaults and the shared response interceptor.
 *
 * @param {Object} params Configuration parameters.
 * @param {Object} params.axiosInstance Axios instance to configure.
 * @param {Object|null} params.router Vue router instance kept for API compatibility.
 * @returns {Object} Configured axios instance.
 */
export function ConfigureAxiosInterceptors({ axiosInstance = axios, router = null } = {}) {
    axiosInstance.defaults.withCredentials = true;
    axiosInstance.defaults.headers.common = {
        ...axiosInstance.defaults.headers.common,
        "X-Requested-With": "XMLHttpRequest",
        Language: document.documentElement.lang,
        currency: window.localStorage.getItem("currency"),
    };

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error && error.config ? error.config : {};
            const status = error && error.response ? error.response.status : null;

            if (
                status === 419 &&
                !originalRequest._retry &&
                !originalRequest._skipCsrfRefresh
            ) {
                originalRequest._retry = true;

                if (!csrfRefreshPromise) {
                    csrfRefreshPromise = axiosInstance
                        .get("/sanctum/csrf-cookie", {
                            _skipCsrfRefresh: true,
                        })
                        .finally(() => {
                            csrfRefreshPromise = null;
                        });
                }

                try {
                    await csrfRefreshPromise;
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    return Promise.reject(AttachApiError(refreshError));
                }
            }

            // A 403 is no longer followed by router.go(). Reloading could create
            // an endless loop while hiding the actual authorization failure.
            void router;

            return Promise.reject(AttachApiError(error));
        }
    );

    return axiosInstance;
}

/**
 * Registers the promotion redirect guard outside app.js.
 *
 * @param {Object} params Guard dependencies.
 * @param {Object} params.router Vue router instance.
 * @param {Object} params.store Vuex store instance.
 * @param {Object} params.i18n Vue i18n instance.
 * @param {Object} params.toast Vue toast service.
 * @returns {void}
 */
export function ConfigurePromotionRedirectGuard({ router, store, i18n, toast }) {
    router.beforeEach((to, from, next) => {
        const incompletePromotionRedirect = GetIncompletePromotionPaymentRedirect({
            to,
            store,
            i18n,
            toast,
        });

        if (incompletePromotionRedirect) {
            return next(incompletePromotionRedirect);
        }

        return next();
    });
}

export default axios;